import { Action, Thunk, ThunkOn, Computed, action, thunk, computed, thunkOn } from 'easy-peasy'
import { BankrateOffer } from '@bankrate/mortgage-types'

import {
  eventTypes,
  EventBeamModel,
  TrackingFieldModel,
  ViewTrackingModel,
  TrackingClickModel,
  ProductTrackingParams,
  TrackingEmailFormModel,
  ProductListViewed,
  ProductListFiltered,
} from 'types/tracking'
import uuid from 'utils/uuid'
import debounce from 'utils/debounce'
import lowerCaseValues from 'utils/lowerCaseValues'
import { getProductAttributes, makeBaseImpression, getProduct } from 'utils/tracking'
import { getOffersCounts, isMessage, isPrimary, isExtra } from 'utils/offers'
import { StoreModel } from '../index'

interface WebElementModel {
  elementType: string
  location: string
  position: string
  htmlId?: string
  text: string
  name: string
}

export interface TrackingModel {
  // State
  correlationIds: string[]
  trackedEvents: EventBeamModel[]

  // Computed
  activeCorrelationId: Computed<TrackingModel, string>

  // Actions
  recordEvent: Action<TrackingModel, EventBeamModel>
  resetEvents: Action<TrackingModel>
  setNewCorrelationIds: Action<TrackingModel>

  // Thunks
  fireBeam: Thunk<TrackingModel, EventBeamModel>
  onInputTracking: Thunk<TrackingModel, TrackingFieldModel, any, StoreModel>
  onClickTracking: Thunk<TrackingModel, TrackingClickModel, any, StoreModel>
  onViewedTracking: Thunk<TrackingModel, ViewTrackingModel, any, StoreModel>
  onProductTracking: Thunk<TrackingModel, ProductTrackingParams, any, StoreModel>
  onEmailFormTracking: Thunk<TrackingModel, TrackingEmailFormModel, any, StoreModel>

  // ThunkOn
  onSeeMore: ThunkOn<TrackingModel>
  onSortBy: ThunkOn<TrackingModel>
}

const tracking: TrackingModel = {
  // State
  correlationIds: [uuid(), uuid()],
  trackedEvents: [],

  // Computed
  activeCorrelationId: computed(
    [(state, storeState) => (storeState as any)['dimensions'].activeDisplayTab, state => state.correlationIds],
    (activeDisplayTab, correlationIds) => {
      const [firstId, secondId] = correlationIds

      return activeDisplayTab === 'featured' ? firstId : secondId
    }
  ),

  // Actions
  recordEvent: action((state, event) => {
    state.trackedEvents.push(event)
  }),

  resetEvents: action(state => {
    state.trackedEvents = []
  }),

  setNewCorrelationIds: action(state => {
    state.correlationIds = [uuid(), uuid()]
  }),

  // Thunks
  /**
   * @desc Will normalize and send tracking event to Tagular while keeping a record of sent events in the store for easy testing.
   * @param eventName The name of the event to send
   * @param eventPayload the corresponding payload
   */
  fireBeam: thunk((actions, { eventName, eventPayload, skipLowerCase }) => {
    const payload = {
      '@type': eventTypes[eventName],
      ...lowerCaseValues(eventPayload, skipLowerCase),
    }

    if (typeof globalThis.tagular === 'function') {
      globalThis.tagular('beam', eventName, payload)
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Fired event:', eventName, payload)
    }

    actions.recordEvent({ eventName, eventPayload: payload })
  }),

  /**
   * Used to fire off events when a user interacts with an input.
   * @param inputField The field object containing details about the input.
   */
  onInputTracking: thunk((actions, { name, label = name, type = 'UNKNOWN', value }, { getState }) => {
    const { activeCorrelationId } = getState()

    debounce(
      () => {
        actions.fireBeam({
          eventName: 'FieldInputted',
          eventPayload: {
            userInputField: {
              fieldName: name,
              fieldType: type,
              fieldLabel: label,
              fieldValue: String(value),
            },
            formContext: {
              formName: 'mortgage',
              formType: 'rate_table',
              formId: activeCorrelationId,
            },
          },
          skipLowerCase: ['fieldType'],
        })
      },
      2000,
      name
    )()
  }),

  /**
   * Used to fire off events for email form tracking.
   * @param payload The object containing: the eventName and stepName for the event
   * and authenticatedContext if the event requires it.
   */
  onEmailFormTracking: thunk((actions, { eventName, stepName, element, authenticatedContext = null }, { getState }) => {
    const { activeCorrelationId } = getState()

    const stepContext = {
      stepType: 'CONFIRMATION',
      stepName: 'Email',
    }

    if (stepName) {
      stepContext['stepName'] = stepName
    }

    let webElement

    if (element) {
      webElement = {
        elementType: element.type,
        location: element.location,
        position: element.position,
        htmlId: element.htmlId || '',
        text: element.text,
        name: element.name,
      } as WebElementModel
    }

    const formContext = { formId: activeCorrelationId, formName: 'instant rate alert', formType: 'AUTHENTICATION' }

    actions.fireBeam({
      eventName,
      eventPayload: {
        formContext,
        stepContext,
        authenticatedContext,
        webElement,
      },
      skipLowerCase: ['name', 'stepContext', 'location'],
    })
  }),

  /**
   * Used to fire off clicks events when a user clicks with a tracked element.
   * @param webElement The field object containing details about the element clicked.
   */
  onClickTracking: thunk((actions, payload) => {
    const additionalSkipLowerCase = payload.additionalSkipLowerCase || []

    actions.fireBeam({
      eventName: 'ElementClicked',
      eventPayload: {
        webElement: {
          elementType: payload.element.type,
          location: payload.element.location,
          position: payload.element.position,
          htmlId: payload.element.htmlId || '',
          text: payload.element.text,
          name: payload.element.name,
        } as WebElementModel,
        actionOutcome: payload.outcome,
      },
      skipLowerCase: ['name', 'actionOutcome', ...additionalSkipLowerCase],
    })
  }),

  /**
   * Used to fire off view events when a see a tracked element.
   * @param webElement The field object containing details about the element viewed.
   */
  onViewedTracking: thunk((actions, payload) => {
    const elementPayload = {
      elementType: payload.element.type,
      location: payload.element.location,
      position: payload.element.position,
      text: payload.element.text,
      name: payload.element.name,
    } as WebElementModel

    if (typeof payload.element.htmlId !== 'undefined') {
      elementPayload['htmlId'] = payload.element.htmlId || ''
    }

    actions.fireBeam({
      eventName: 'ElementViewed',
      eventPayload: {
        webElement: elementPayload,
      },
      skipLowerCase: ['name'],
    })
  }),

  /**
   * Used to fire off events when users interact with a product
   * @param eventName The name of the impression event to fire
   * @param position Optional - The position of the offer in the second argument
   * @param offer Optional - the offer to track for ProductViewed and ProductClicked
   * @param offer Optional - the correlation id for ProductClicked
   */
  onProductTracking: thunk((actions, { eventName, offer, position, id, text }, { getStoreState, getState }) => {
    const activeCorrelationId = getState().activeCorrelationId
    const offers = getStoreState().offers.offers
    const { noCoverageReason, sortBy } = getStoreState().offers
    const { dimensions } = getStoreState().dimensions

    const calculatedLoanAmount = getStoreState().dimensions.calculatedLoanAmount
    const activeDisplayTab = getStoreState().dimensions.activeDisplayTab

    const primaryOffers = offers.filter(isPrimary)
    const extraOffers = offers.filter(isExtra)
    const displayOffers = offers.filter(offer => !isMessage(offer))

    const hasOnlyAdditionalOffers = primaryOffers.length === 0 && extraOffers.length > 0
    const offersToCount = hasOnlyAdditionalOffers ? extraOffers : displayOffers.filter(offer => !isExtra(offer))
    const counts = getOffersCounts(offersToCount)

    let listName = activeDisplayTab !== 'all' ? 'featured lenders' : 'all products'

    if (hasOnlyAdditionalOffers) {
      listName = 'additional rates'
    }

    let baseImpression

    switch (eventName) {
      case 'ProductClicked':
        return actions.fireBeam({
          eventName,
          eventPayload: {
            listId: activeCorrelationId,
            correlationId: id,
            product: getProduct(
              offer as BankrateOffer,
              position as number,
              dimensions.mortgageType,
              activeDisplayTab,
              text
            ),
          },
          skipLowerCase: ['productVertical'],
        })

      case 'ProductAttributesCaptured':
        return actions.fireBeam({
          eventName,
          eventPayload: {
            listId: activeCorrelationId,
            products: getProductAttributes(displayOffers, calculatedLoanAmount),
          },
          skipLowerCase: ['productVertical'],
        })

      case 'ProductViewed':
        return actions.fireBeam({
          eventName,
          eventPayload: {
            listId: activeCorrelationId,
            product: getProduct(offer as BankrateOffer, position as number, dimensions.mortgageType, activeDisplayTab),
          },
          skipLowerCase: ['productVertical'],
        })

      case 'ProductListViewed':
        baseImpression = makeBaseImpression(
          {
            offers: displayOffers,
            dimensions,
            trackingInfo: {
              listId: activeCorrelationId,
            },
          },
          activeDisplayTab
        )
        return actions.fireBeam({
          eventName: 'ProductListViewed',
          skipLowerCase: ['location', 'fieldType'],
          eventPayload: {
            ...baseImpression,
            listName,
            category: dimensions.mortgageType,
            featuredListings: counts.featured,
            standardListings: counts.standard,
            sponsoredListings: counts.sponsored,
            editorialListings: counts.editorial,
            paidListings: counts.total - counts.editorial,
            reasonNoCoverage: noCoverageReason,
          } as ProductListViewed,
        })

      case 'ProductListFiltered':
        baseImpression = makeBaseImpression(
          {
            offers: displayOffers,
            dimensions,
            trackingInfo: {
              listId: activeCorrelationId,
            },
          },
          activeDisplayTab
        )
        return actions.fireBeam({
          eventName: 'ProductListFiltered',
          skipLowerCase: ['location', 'fieldType'],
          eventPayload: {
            ...baseImpression,
            sorts: [{ type: 'sort', value: sortBy }],
          } as ProductListFiltered,
        })
    }
  }),

  // ThunkOn
  onSeeMore: thunkOn(
    (actions, storeActions) => (storeActions as any)['offers'].showMoreOffers,
    actions => {
      actions.onProductTracking({ eventName: 'ProductListViewed' })
      actions.onProductTracking({ eventName: 'ProductAttributesCaptured' })
    }
  ),

  onSortBy: thunkOn(
    (actions, storeActions) => (storeActions as any)['offers'].setSortBy,
    actions => {
      actions.onProductTracking({ eventName: 'ProductListFiltered' })
      actions.onProductTracking({ eventName: 'ProductAttributesCaptured' })
    }
  ),
}

export default tracking
